import React, { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';

import { findAncestor } from '@candu-ai/placement/utils';

import { LayerContext } from './LayerContext';
import { initialInteractionState, InteractionType, reducer } from './reducer';

import { isSelectableNode } from '../utils';

export type InteractionLayerProps = {
  selectedNode?: string;
  hoveredNode?: string;
  onSelectNode?: (nodeId: string) => void;
  onClickNode?: (nodeId?: string) => void;
  onHoverNode?: (nodeId?: string) => void;
  onConfirmPlacement?: () => void;
  domLayerCanvasRef: React.RefObject<HTMLDivElement>;
  panelElement?: HTMLElement;
  children: React.ReactNode;
  context?: {
    disableScrollForHover?: boolean; // view is minmized
    disableScrollForSelect?: boolean; // view is minmized
  };
};
const AVG_ANIMATION_REFRESH_CYCLE = 16;
export const InteractionLayer: React.FC<InteractionLayerProps> = ({
  children,
  context: appContext,
  domLayerCanvasRef,
  onSelectNode,
  onClickNode,
  onHoverNode,
  onConfirmPlacement,
  hoveredNode,
  panelElement,
  selectedNode,
}: InteractionLayerProps) => {
  const [state, dispatch] = useReducer(reducer, initialInteractionState);
  const thresholdRef = useRef<number>(0);
  const keyPressedRef = useRef<string>();

  const handleMouseOver = useCallback(
    (event: MouseEvent) => {
      const hasMovedOverThreshold = Date.now() > thresholdRef.current + AVG_ANIMATION_REFRESH_CYCLE;
      // do not fire too many times the mouseover event
      event.preventDefault();
      if (!hasMovedOverThreshold) {
        return;
      }
      thresholdRef.current = Date.now();

      const hasNoActionAncestor = findAncestor(
        event.target as Element | null,
        `[data-placement-id]`,
      );

      const targetId = hasNoActionAncestor?.getAttribute('data-placement-id');

      const isSelectable = isSelectableNode(hasNoActionAncestor);
      if (targetId && isSelectable && targetId !== state.hoveredNodeId) {
        dispatch({
          type: InteractionType.Hovered,
          payload: {
            hoveredNodeId: targetId,
          },
        });
        onHoverNode?.(targetId);
      }
    },

    [onHoverNode, state.hoveredNodeId, state.isOverDomLayer, dispatch],
  );

  const handleMouseEnterPanel = useCallback(
    (event: MouseEvent) => {
      event.preventDefault();
      dispatch({
        type: InteractionType.OverDOMLayer,
        payload: {
          isOverDomLayer: true,
        },
      });
    },

    [dispatch],
  );

  const handleMouseLeavePanel = useCallback(
    (event: MouseEvent) => {
      event.preventDefault();
      dispatch({
        type: InteractionType.OverDOMLayer,
        payload: {
          isOverDomLayer: false,
        },
      });
    },

    [dispatch],
  );
  const handleFocus = useCallback(
    (e: React.FocusEvent) => {
      if (keyPressedRef.current !== 'Tab') {
        return;
      }
      const focusedNodeId = (e.target as HTMLElement).getAttribute('data-placement-id');
      const isSelectable = isSelectableNode(e.target);

      if (focusedNodeId && isSelectable) {
        onSelectNode?.(focusedNodeId);
      }
    },
    [onSelectNode, keyPressedRef.current],
  );

  const handleSelect = useCallback(
    (event: React.MouseEvent) => {
      const hasNoActionAncestor = findAncestor(
        event.target as Element | null,
        `[data-placement-id]`,
      );
      const selectedNodeId = hasNoActionAncestor?.getAttribute('data-placement-id');
      const isSelectable = isSelectableNode(hasNoActionAncestor);
      if (isSelectable && selectedNodeId) {
        onSelectNode?.(selectedNodeId);
      }
    },
    [onSelectNode],
  );

  const handleClick = useCallback(
    (event: MouseEvent) => {
      const isIconClicked = findAncestor(event.target as Element | null, `[data-placement-toggle]`);
      if (isIconClicked) {
        event.preventDefault();
        return;
      }

      const isConfirmPlacement = findAncestor(
        event.target as Element | null,
        `[data-confirm-placement]`,
      );

      if (isConfirmPlacement && onConfirmPlacement) {
        event.preventDefault();
        onConfirmPlacement();
        return;
      }

      const hasNoActionAncestor = findAncestor(
        event.target as Element | null,
        `[data-placement-id]`,
      );
      const clickedNodeId = hasNoActionAncestor?.getAttribute('data-placement-id');
      const isSelectable = isSelectableNode(hasNoActionAncestor);
      if (isSelectable && clickedNodeId) {
        onClickNode?.(clickedNodeId);
      }
    },
    [onClickNode],
  );

  // this is for storing the key pressed and ensure not double callback event is fired on click (because click event will trigger onClick and onFocus)
  const handleKeydown = useCallback((e: KeyboardEvent) => {
    keyPressedRef.current = e.code;
  }, []);
  const handleKeyup = useCallback(() => {
    keyPressedRef.current = undefined;
  }, []);

  useEffect(() => {
    const nodeLayerCanvas = domLayerCanvasRef.current;

    document?.addEventListener('keydown', handleKeydown);
    document?.addEventListener('keyup', handleKeyup);

    nodeLayerCanvas?.addEventListener('mouseover', handleMouseOver);
    nodeLayerCanvas?.addEventListener('click', handleClick);

    // keep track of whether we are over the extension panel
    panelElement?.addEventListener('mouseenter', handleMouseEnterPanel);
    panelElement?.addEventListener('mouseleave', handleMouseLeavePanel);

    return () => {
      nodeLayerCanvas?.removeEventListener('mouseover', handleMouseOver);

      nodeLayerCanvas?.removeEventListener('click', handleClick);

      panelElement?.removeEventListener('mouseenter', handleMouseEnterPanel);
      panelElement?.removeEventListener('mouseleave', handleMouseLeavePanel);

      document?.removeEventListener('keydown', handleKeydown);
      document?.removeEventListener('keyup', handleKeyup);
    };
  }, [handleMouseOver, handleMouseEnterPanel, handleMouseLeavePanel, handleClick]);

  useEffect(() => {
    if (selectedNode) {
      const nodeElements = domLayerCanvasRef.current?.querySelectorAll(
        `[data-placement-id='${selectedNode}']`,
      );
      if (nodeElements?.[0] && !appContext?.disableScrollForSelect) {
        nodeElements?.[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [selectedNode, dispatch, appContext?.disableScrollForSelect]);

  useEffect(() => {
    if (hoveredNode) {
      dispatch({
        type: InteractionType.Hovered,
        payload: {
          hoveredNodeId: hoveredNode,
        },
      });
      const nodeElements = domLayerCanvasRef.current?.querySelectorAll(
        `[data-placement-id='${hoveredNode}']`,
      );
      if (nodeElements?.[0] && !appContext?.disableScrollForHover) {
        nodeElements?.[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [hoveredNode, dispatch, appContext?.disableScrollForHover]);

  const context = useMemo(
    () => ({
      selectedNodeId: selectedNode,
      hoveredNodeId: state.hoveredNodeId,
      isOverDomLayer: panelElement ? state.isOverDomLayer : false,
      handleFocus,
      handleSelect,
    }),
    [selectedNode, state.hoveredNodeId, state.isOverDomLayer, handleFocus, handleSelect],
  );

  return <LayerContext.Provider value={context}>{children}</LayerContext.Provider>;
};
